import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import { useTheme } from 'styled-components'

import { NumberDirection } from 'common-constants/numbers'
import { useStoreFrontGiftsUrl } from 'components/block/Profile/ProfileMain/hooks/useStoreFrontGiftsUrl'
import { GiftPlaceholder } from 'components/page/ForeignProfile/Gifts/GiftPlaceholder'
import {
  LeftGradient,
  RightGradient,
  WrapperGiftMask,
  GiftsScrollBlock,
  WrapperGiftsWithLink,
  WrapperLink,
  WrapperRelative,
} from 'components/page/ForeignProfile/Gifts/Gifts.styled'
import { GiftsList } from 'components/page/ForeignProfile/Gifts/GiftsList'
import { PresentVip } from 'components/page/ForeignProfile/Gifts/PresentVip/PresentVip'
import { isDraggableDevice } from 'components/page/PhotoRating/function/isDraggableDevice'
import { ProfileContext } from 'components/page/UserProfile/Context/CreateProfileContext'
import {
  ArrowLeft,
  ArrowRight,
  Arrows,
} from 'components/presentational/Arrows/Arrows'
import { GQLGender } from 'generated-graphql/graphql'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

import { GQLForeignRootQuery } from '../gql/__generated__/ForeignRoot'

/**
 *
 * @param giftsProfile
 * @param gender
 * @param userId
 * @param presentVip
 * @constructor
 */
export const GiftsIndexView: FC<{
  gender: GQLGender
  gifts: NonNullable<GQLForeignRootQuery['user']>['gifts']['gifts']['nodes']
  giftsCommonFormat: GQLForeignRootQuery['ui']['giftFormats']
  presentVip?: NonNullable<GQLForeignRootQuery['user']>['vip']['presentVip']
}> = ({ gifts, giftsCommonFormat, gender, presentVip }) => {
  const giftListRef = useRef<HTMLDivElement>(null)
  const [hasArrowLeft, setShowLeftArrow] = useState<boolean>(false)
  const [hasArrowRight, setShowRightArrow] = useState<boolean>(false)
  const [disabledClick, setDisabledClick] = useState(false)
  const { multiplier } = useTheme()
  const { profileId } = useContext(ProfileContext)
  const giftsUrl = useStoreFrontGiftsUrl(profileId)
  const countGifts = gifts.length + (presentVip ? 1 : 0)

  const { authorized, javaScriptEnabled } = useShallowEqualSelector(
    ({
      authorizationReducer: { authorized },
      systemReducer: { javaScriptEnabled },
    }) => ({ authorized, javaScriptEnabled })
  )

  const movePointer = useCallback(
    (direction: NumberDirection) => {
      const _direction = direction * multiplier
      const parent = giftListRef.current

      if (parent && !disabledClick) {
        setDisabledClick(true)

        const scrollX = (parent.scrollWidth / countGifts) * _direction

        giftListRef.current.scroll({
          left: giftListRef.current.scrollLeft + scrollX,
          behavior: 'smooth',
        })
      }
    },
    [countGifts, disabledClick, multiplier]
  )

  const handleScroll = useCallback(() => {
    const parent = giftListRef.current

    setDisabledClick(true)

    if (parent) {
      const isStartScroll = parent.scrollLeft === 0

      const isEndScroll =
        Math.abs(parent.scrollLeft) === parent.scrollWidth - parent.offsetWidth

      if (isStartScroll) {
        setShowLeftArrow(false)
      } else {
        if (!hasArrowLeft) {
          setShowLeftArrow(true)
        }
      }

      if (isEndScroll) {
        setShowRightArrow(false)
      } else {
        if (!hasArrowRight) {
          setShowRightArrow(true)
        }
      }
    }

    /**
     * Можно попробовать использовать scrollEnd, но пока не работает в Safari(iOS, MacOs)
     */
    requestAnimationFrame(() => setDisabledClick(false))
  }, [hasArrowLeft, hasArrowRight])

  useEffect(() => {
    const parent = giftListRef.current

    if (parent) {
      const hasScroll = parent.scrollWidth > parent.offsetWidth

      if (hasScroll) {
        setShowRightArrow(true)
      }
    }
  }, [])

  return (
    <WrapperGiftsWithLink>
      <WrapperRelative>
        <WrapperGiftMask>
          <LeftGradient />
          <GiftsScrollBlock ref={giftListRef} onScroll={handleScroll}>
            {presentVip && (
              <PresentVip
                presenter={presentVip.presenter}
                days={presentVip.days}
                hidden={presentVip.hidden}
              />
            )}
            <GiftsList
              gifts={gifts}
              giftsCommonFormat={giftsCommonFormat}
              parent={giftListRef}
            />
          </GiftsScrollBlock>
          <Arrows
            isShow={authorized && javaScriptEnabled && !isDraggableDevice()}
            isArrowLeftShow={hasArrowLeft}
            isArrowRightShow={hasArrowRight}
            arrowLeft={
              <ArrowLeft
                onMoveLeft={() => movePointer(NumberDirection.left)}
                size={24}
              />
            }
            arrowRight={
              <ArrowRight
                onMoveRight={() => movePointer(NumberDirection.right)}
                size={24}
              />
            }
          />
          <RightGradient />
        </WrapperGiftMask>
      </WrapperRelative>
      <WrapperLink to={giftsUrl} rel="nofollow" data-name="gifts-block-action">
        <GiftPlaceholder gender={gender} />
      </WrapperLink>
    </WrapperGiftsWithLink>
  )
}
